import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Building2, GitBranch, Pencil, Save } from "lucide-react";
import { formatApiError } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

const EMPTY_FORM = {
  name: "", client_code: "", gstin: "", pan: "",
  contact_person: "", contact_phone: "", contact_email: "",
  fy_start_month: 4, notes: "",
  has_branches: false, branches: [{ name: "" }],
};

export default function CompanyManager({ onCompanyChange }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpenForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      client_code: c.client_code || "",
      gstin: c.gstin || "",
      pan: c.pan || "",
      contact_person: c.contact_person || "",
      contact_phone: c.contact_phone || "",
      contact_email: c.contact_email || "",
      fy_start_month: c.fy_start_month || 4,
      notes: c.notes || "",
      has_branches: !!c.has_branches,
      branches: (c.branches && c.branches.length) ? c.branches.map((b) => ({ name: b.name, branch_id: b.branch_id })) : [{ name: "" }],
    });
    setOpenForm(true);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      client_code: form.client_code.trim() || null,
      gstin: form.gstin.trim().toUpperCase() || null,
      pan: form.pan.trim().toUpperCase() || null,
      contact_person: form.contact_person.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      contact_email: form.contact_email.trim() || null,
      fy_start_month: Number(form.fy_start_month) || 4,
      notes: form.notes.trim() || null,
      has_branches: form.has_branches,
      branches: form.has_branches
        ? form.branches.filter((b) => b.name.trim()).map((b) => ({ name: b.name.trim(), branch_id: b.branch_id || "" }))
        : [],
    };
    try {
      if (editing) {
        await axios.put(`${API}/companies/${editing.company_id}`, payload);
        toast.success(`Client "${payload.name}" updated`);
      } else {
        await axios.post(`${API}/companies`, payload);
        toast.success(`Client "${payload.name}" created`);
      }
      setOpenForm(false);
      fetchCompanies();
      onCompanyChange && onCompanyChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and ALL associated accounting data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/companies/${c.company_id}`);
      toast.success(`Client "${c.name}" deleted`);
      fetchCompanies();
      onCompanyChange && onCompanyChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="company-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-[Chivo] font-semibold tracking-tight">Clients</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {companies.length} client{companies.length === 1 ? "" : "s"} on file. Each client has isolated COA, TB, vouchers, and MIS.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="btn-new-company" className="bg-zinc-950 text-white hover:bg-zinc-800">
          <Plus className="w-4 h-4 mr-2" /> Add Client
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All clients</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="companies-table">
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Branches</TableHead>
                <TableHead className="text-right w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-6">Loading…</TableCell></TableRow>
              )}
              {!loading && companies.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-6">No clients yet — click "Add Client" to get started.</TableCell></TableRow>
              )}
              {!loading && companies.map((c) => (
                <TableRow key={c.company_id} data-testid={`company-row-${c.company_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-zinc-400" />
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{c.name}</div>
                        <div className="text-[10px] font-[IBM_Plex_Mono] text-zinc-400">{c.company_id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs font-[IBM_Plex_Mono] text-zinc-700">{c.client_code || "—"}</TableCell>
                  <TableCell className="text-xs font-[IBM_Plex_Mono] text-zinc-700">{c.gstin || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {c.contact_person ? (
                      <div>
                        <div className="text-zinc-800">{c.contact_person}</div>
                        {c.contact_phone && <div className="text-zinc-500">{c.contact_phone}</div>}
                      </div>
                    ) : <span className="text-zinc-400">—</span>}
                  </TableCell>
                  <TableCell>
                    {c.has_branches && c.branches?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {c.branches.map((b, bi) => (
                          <span key={b.branch_id || b.name || bi} className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <GitBranch className="w-2.5 h-2.5" /> {b.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">Single branch</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-client-${c.company_id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {c.company_id !== "default" && (
                        <Button
                          size="sm" variant="outline"
                          className="text-rose-600 hover:bg-rose-50"
                          onClick={() => handleDelete(c)}
                          data-testid={`delete-client-${c.company_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit client — ${editing.name}` : "Add client"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Client name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Marvel Industries Pvt Ltd" data-testid="input-client-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Client code</Label>
                <Input value={form.client_code} onChange={(e) => setForm({ ...form, client_code: e.target.value })} placeholder="e.g., MIPL-001" data-testid="input-client-code" />
              </div>
              <div className="space-y-1.5">
                <Label>FY start month</Label>
                <Select value={String(form.fy_start_month)} onValueChange={(v) => setForm({ ...form, fy_start_month: Number(v) })}>
                  <SelectTrigger data-testid="input-fy-start"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (<SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>GSTIN</Label>
                <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="29AABCU9603R1ZX" data-testid="input-gstin" />
              </div>
              <div className="space-y-1.5">
                <Label>PAN</Label>
                <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="AABCU9603R" data-testid="input-pan" />
              </div>
              <div className="space-y-1.5">
                <Label>Contact person</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="input-contact-person" />
              </div>
              <div className="space-y-1.5">
                <Label>Contact phone</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="input-contact-phone" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Contact email</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="input-contact-email" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-notes" />
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox" id="has-branches"
                  checked={form.has_branches}
                  onChange={(e) => setForm({ ...form, has_branches: e.target.checked })}
                  className="rounded border-zinc-300"
                  data-testid="check-has-branches"
                />
                <label htmlFor="has-branches" className="text-sm text-zinc-700">This client has multiple branches</label>
              </div>
              {form.has_branches && (
                <div className="space-y-2 pl-6">
                  {form.branches.map((b, i) => (
                    <div key={b.branch_id || i} className="flex items-center gap-2">
                      <Input
                        value={b.name}
                        onChange={(e) => {
                          const nb = [...form.branches];
                          nb[i] = { ...nb[i], name: e.target.value };
                          setForm({ ...form, branches: nb });
                        }}
                        placeholder={`Branch ${i + 1} name`}
                        data-testid={`input-branch-${i}`}
                      />
                      {form.branches.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() =>
                          setForm({ ...form, branches: form.branches.filter((_, j) => j !== i) })
                        } className="text-rose-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() =>
                    setForm({ ...form, branches: [...form.branches, { name: "" }] })
                  }>
                    <Plus className="w-3 h-3 mr-1" /> Add branch
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.name.trim()} data-testid="btn-save-client">
              <Save className="w-4 h-4 mr-1.5" /> {editing ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
